'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, Flame, Mic, MicOff, PhoneOff, Plus, Target, Trophy, Users, X, Activity as ActivityIcon, BarChart3 } from 'lucide-react'
import { firebaseAuth } from '@/lib/firebase'
import { collection, deleteDoc, doc, firestore, getDocs, onSnapshot, query, serverTimestamp, setDoc, where } from '@/lib/db'
import { CoinIcon } from '@/components/coin-icon'
import { useGroupVoice } from '@/components/ludo-voice'
import { PosterIcon, EmojiPoster } from '@/components/poster-icons'

// =============================================================================================
// HUB: Daily Missions, Weekly Tournament, Streak Rewards, Friends Activity (opened from the Games page),
// plus Chat Polls and the Group Voice Room (opened from a chat). Everything that pays coins or ranks people is computed on the
// SERVER (/api/missions, /api/weekly); the screens below only display it.
// =============================================================================================

export async function hubApi(path: string, body: Record<string, unknown>): Promise<any> {
  try {
    const token = await firebaseAuth.currentUser?.getIdToken()
    if (!token) return { error: 'Please sign in again.' }
    const res = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) })
    return (await res.json().catch(() => null)) || { error: 'failed' }
  } catch { return { error: 'Network problem. Try again.' } }
}

function Sheet({ title, icon, onClose, children }: { title: string; icon?: React.ReactNode; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="hub-bg" onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div className="hub-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="hub-head"><strong>{icon}{title}</strong><button type="button" className="hub-x" onClick={onClose} aria-label="Close"><X size={18} /></button></div>
        {children}
      </div>
    </div>
  )
}

const Av = ({ src, name, size = 34 }: { src?: string; name: string; size?: number }) => (
  <span className="hub-av" style={{ width: size, height: size, flex: `0 0 ${size}px`, fontSize: size * 0.42 }}>
    {src ? <img src={src} alt="" loading="lazy" decoding="async" draggable={false} referrerPolicy="no-referrer" /> : <b>{(name || '?').slice(0, 1).toUpperCase()}</b>}
  </span>
)
const ago = (ms: number) => { const m = Math.max(1, Math.round((Date.now() - ms) / 60000)); return m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago` }
const GAME_LABEL: Record<string, string> = { ludo: 'Ludo', chess: 'Chess', carrom: 'Carrom' }

/** The 4 quick buttons on the Games page. */
export function GamesHubBar({ streak, friends, onViewProfile, onCoins }: {
  streak: number; friends: { id: string; name: string; avatar: string }[]; onViewProfile?: (uid: string) => void; onCoins?: (n: number, label: string) => void
}) {
  const [open, setOpen] = useState<'' | 'missions' | 'weekly' | 'streak' | 'activity'>('')
  const btn = (k: typeof open, icon: React.ReactNode, label: string) => (
    <button type="button" className="hub-chipbtn" onClick={() => setOpen(k)}>{icon}<span>{label}</span></button>
  )
  return (
    <>
      <div className="hub-bar">
        {btn('missions', <Target size={16} />, 'Missions')}
        {btn('weekly', <Trophy size={16} />, 'Weekly')}
        {btn('streak', <Flame size={16} />, 'Streak')}
        {btn('activity', <ActivityIcon size={16} />, 'Activity')}
      </div>
      {open === 'missions' && <MissionsSheet onClose={() => setOpen('')} onCoins={onCoins} />}
      {open === 'weekly' && <WeeklySheet onClose={() => setOpen('')} onCoins={onCoins} onViewProfile={onViewProfile} />}
      {open === 'streak' && <StreakSheet streak={streak} onClose={() => setOpen('')} />}
      {open === 'activity' && <ActivitySheet friends={friends} onClose={() => setOpen('')} onViewProfile={onViewProfile} />}
    </>
  )
}

// ---------------------------------------------------------------- Daily Missions
type Mission = { id: string; label: string; target: number; coins: number; progress: number; claimed: boolean }
export function MissionsSheet({ onClose, onCoins }: { onClose: () => void; onCoins?: (n: number, label: string) => void }) {
  const [list, setList] = useState<Mission[] | null>(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState('')
  const load = useCallback(() => { void hubApi('/api/missions', { action: 'status' }).then((r) => { if (r.missions) setList(r.missions); else setErr(r.error || 'Could not load missions.') }) }, [])
  useEffect(() => { load() }, [load])
  const claim = async (m: Mission) => {
    setBusy(m.id)
    const r = await hubApi('/api/missions', { action: 'claim', id: m.id })
    setBusy('')
    if (r.granted > 0) { onCoins?.(r.granted, 'Mission reward'); if (r.missions) setList(r.missions) } else if (r.already) load()
    else setErr(r.error === 'not-done' ? 'Finish the mission first.' : r.error || 'Could not claim.')
  }
  return (
    <Sheet title="Daily missions" icon={<Target size={16} />} onClose={onClose}>
      <p className="hub-note">Finish real online games (not bot games). Missions reset every day at 00:00 UTC.</p>
      {err && <p className="hub-err">{err}</p>}
      {!list && !err && <p className="hub-note">Loading…</p>}
      {list?.map((m) => {
        const done = m.progress >= m.target
        return (
          <div key={m.id} className="hub-row">
            <div className="hub-grow">
              <b>{m.label}</b>
              <div className="hub-bar-track"><i style={{ width: `${Math.round((m.progress / m.target) * 100)}%` }} /></div>
              <small>{m.progress}/{m.target}</small>
            </div>
            {m.claimed ? <span className="hub-done"><Check size={14} /> Done</span>
              : <button type="button" className="hub-claim" disabled={!done || busy === m.id} onClick={() => void claim(m)}><CoinIcon size={14} /> +{m.coins}</button>}
          </div>
        )
      })}
    </Sheet>
  )
}

// ---------------------------------------------------------------- Weekly Tournament
type WRow = { rank: number; uid: string; name: string; avatar: string; wins: number; played: number }
export function WeeklySheet({ onClose, onCoins, onViewProfile }: { onClose: () => void; onCoins?: (n: number, label: string) => void; onViewProfile?: (uid: string) => void }) {
  const [d, setD] = useState<any>(null)
  const [err, setErr] = useState('')
  const load = useCallback(() => { void hubApi('/api/weekly', { action: 'board' }).then((r) => { if (r.top) setD(r); else setErr(r.error || 'Could not load the board.') }) }, [])
  useEffect(() => { load() }, [load])
  const left = d ? Math.max(0, d.endsMs - Date.now()) : 0
  const days = Math.floor(left / 86_400_000); const hrs = Math.floor((left % 86_400_000) / 3_600_000)
  const claim = async () => {
    const r = await hubApi('/api/weekly', { action: 'claim' })
    if (r.granted > 0) { onCoins?.(r.granted, 'Tournament prize'); load() } else setErr(r.error || 'Nothing to claim.')
  }
  return (
    <Sheet title="Weekly tournament" icon={<Trophy size={16} />} onClose={onClose}>
      {err && <p className="hub-err">{err}</p>}
      {!d && !err && <p className="hub-note">Loading…</p>}
      {d && (
        <>
          <p className="hub-note">Most online wins this week. Ends in {days}d {hrs}h. Top 3 (min. {d.minWins} wins) win {d.prizes.join(' / ')} coins - claim it next week.</p>
          {d.last && (
            <div className="hub-banner">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><PosterIcon name="trophy" size={18} /> Last week you finished #{d.last.rank} ({d.last.wins} wins)</span>
              {d.last.claimed ? <span className="hub-done"><Check size={14} /> Claimed</span> : <button type="button" className="hub-claim" onClick={() => void claim()}><CoinIcon size={14} /> +{d.last.coins}</button>}
            </div>
          )}
          {d.me && <div className="hub-me">You: {d.me.rank > 0 ? `#${d.me.rank}` : 'unranked'} · {d.me.wins} wins · {d.me.played} played</div>}
          {d.top.length === 0 && <p className="hub-note">No wins yet this week - be the first!</p>}
          {d.top.map((r: WRow) => (
            <button key={r.uid} type="button" className="hub-row hub-btnrow" onClick={() => onViewProfile?.(r.uid)}>
              <span className={`hub-rank ${r.rank <= 3 ? `r${r.rank}` : ''}`}>{r.rank}</span>
              <Av src={r.avatar} name={r.name} />
              <span className="hub-grow"><b>{r.name}</b><small>{r.played} played</small></span>
              <b>{r.wins} wins</b>
            </button>
          ))}
        </>
      )}
    </Sheet>
  )
}

// ---------------------------------------------------------------- Streak Rewards
const STREAK_STEPS = [3, 7, 14, 30, 60, 100]
export function StreakSheet({ streak, onClose }: { streak: number; onClose: () => void }) {
  const next = STREAK_STEPS.find((s) => s > streak)
  const days = Array.from({ length: 7 }, (_, i) => i + 1)
  const inWeek = streak === 0 ? 0 : ((streak - 1) % 7) + 1
  return (
    <Sheet title="Streak rewards" icon={<Flame size={16} />} onClose={onClose}>
      <div className="hub-streak"><span><PosterIcon name="fire" size={34} /></span><b>{streak}</b><small>day streak</small></div>
      <p className="hub-note">Open Animora every day to keep it going. Miss a day and it starts again from 1.</p>
      <div className="hub-week">{days.map((n) => <i key={n} className={n <= inWeek ? 'on' : ''}>{n <= inWeek ? <Check size={12} /> : n}</i>)}</div>
      <div className="gm-sheet-h hub-h">Milestones</div>
      {STREAK_STEPS.map((s) => (
        <div key={s} className="hub-row">
          <span className="hub-ico"><PosterIcon name={streak >= s ? 'check' : 'lock'} size={24} /></span>
          <span className="hub-grow"><b>{s} day streak</b><small>{streak >= s ? 'Reached' : `${s - streak} more day${s - streak === 1 ? '' : 's'}`}</small></span>
        </div>
      ))}
      {next && <p className="hub-note">Next milestone: {next} days.</p>}
    </Sheet>
  )
}

// ---------------------------------------------------------------- Friends Activity
type Item = { key: string; uid: string; name: string; avatar: string; text: string; at: number }
export function ActivitySheet({ friends, onClose, onViewProfile }: { friends: { id: string; name: string; avatar: string }[]; onClose: () => void; onViewProfile?: (uid: string) => void }) {
  const [items, setItems] = useState<Item[] | null>(null)
  const byId = useMemo(() => new Map(friends.map((f) => [f.id, f])), [friends])
  useEffect(() => {
    let live = true
    ;(async () => {
      const ids = friends.map((f) => f.id).slice(0, 30)
      if (!ids.length) { if (live) setItems([]); return }
      const out: Item[] = []
      const wins = await hubApi('/api/activity', { uids: ids })
      for (const w of (wins.items || []) as any[]) {
        const f = byId.get(w.uid)
        out.push({ key: `w${w.uid}${w.at}`, uid: w.uid, name: f?.name || w.name, avatar: f?.avatar || w.avatar, text: `won a game of ${GAME_LABEL[w.game] || 'a game'}`, at: w.at })
      }
      try {
        const snap = await getDocs(query(collection(firestore, 'posts'), where('authorId', 'in', ids.slice(0, 10))))
        snap.docs.forEach((x: any) => {
          const p = x.data(); const at = p.createdAt?.toMillis?.() ?? 0
          if (!at || Date.now() - at > 7 * 86_400_000) return
          const f = byId.get(String(p.authorId))
          out.push({ key: `p${x.id}`, uid: String(p.authorId), name: f?.name || String(p.authorName || 'Friend'), avatar: f?.avatar || String(p.authorAvatar || ''), text: p.mediaType === 'video' && p.postKind !== 'post' ? 'posted a new reel' : 'shared a new post', at })
        })
      } catch { /* posts are a bonus - wins alone are fine */ }
      out.sort((a, b) => b.at - a.at)
      if (live) setItems(out.slice(0, 40))
    })()
    return () => { live = false }
  }, [friends, byId])
  return (
    <Sheet title="Friends activity" icon={<ActivityIcon size={16} />} onClose={onClose}>
      {!items && <p className="hub-note">Loading…</p>}
      {items && items.length === 0 && <p className="hub-note">Nothing new from your friends this week. Challenge one to a game!</p>}
      {items?.map((i) => (
        <button key={i.key} type="button" className="hub-row hub-btnrow" onClick={() => onViewProfile?.(i.uid)}>
          <Av src={i.avatar} name={i.name} size={38} />
          <span className="hub-grow"><b>{i.name}</b> <span className="hub-muted">{i.text}</span><small>{ago(i.at)}</small></span>
        </button>
      ))}
    </Sheet>
  )
}

// ---------------------------------------------------------------- Chat polls
export type PollData = { question: string; options: string[] }

export function PollComposer({ onClose, onSend }: { onClose: () => void; onSend: (q: string, options: string[]) => void }) {
  const [q, setQ] = useState('')
  const [opts, setOpts] = useState(['', ''])
  const clean = opts.map((o) => o.trim()).filter(Boolean)
  const ok = q.trim().length > 0 && clean.length >= 2
  return (
    <Sheet title="Create poll" icon={<BarChart3 size={16} />} onClose={onClose}>
      <input className="hub-input" placeholder="Ask a question" maxLength={200} value={q} onChange={(e) => setQ(e.target.value)} />
      {opts.map((o, i) => (
        <input key={i} className="hub-input" placeholder={`Option ${i + 1}`} maxLength={80} value={o} onChange={(e) => setOpts((cur) => cur.map((x, j) => (j === i ? e.target.value : x)))} />
      ))}
      {opts.length < 6 && <button type="button" className="hub-link" onClick={() => setOpts((c) => [...c, ''])}><Plus size={14} /> Add option</button>}
      <button type="button" className="hub-primary" disabled={!ok} onClick={() => { onSend(q.trim(), clean); onClose() }}>Send poll</button>
    </Sheet>
  )
}

/** One poll inside a chat bubble. Votes live in chats/{chatId}/pollVotes/{messageId}_{uid} (one per person, changeable). */
export function PollBubble({ chatId, messageId, poll, meUid }: { chatId: string; messageId: string; poll: PollData; meUid: string }) {
  const [votes, setVotes] = useState<Record<string, number>>({}) // uid -> option
  useEffect(() => {
    if (!chatId || !messageId || messageId.startsWith('pending-')) return
    return onSnapshot(query(collection(firestore, 'chats', chatId, 'pollVotes'), where('messageId', '==', messageId)), (snap: any) => {
      const next: Record<string, number> = {}
      snap.docs.forEach((x: any) => { const v = x.data(); if (typeof v.userId === 'string' && Number.isInteger(v.option)) next[v.userId] = v.option })
      setVotes(next)
    }, () => undefined)
  }, [chatId, messageId])
  const total = Object.keys(votes).length
  const mine = votes[meUid]
  const vote = (i: number) => {
    if (messageId.startsWith('pending-')) return
    const ref = doc(firestore, 'chats', chatId, 'pollVotes', `${messageId}_${meUid}`)
    if (mine === i) { setVotes((v) => { const n = { ...v }; delete n[meUid]; return n }); void deleteDoc(ref).catch(() => undefined); return }
    setVotes((v) => ({ ...v, [meUid]: i }))
    void setDoc(ref, { userId: meUid, messageId, option: i, updatedAt: serverTimestamp() }).catch(() => undefined)
  }
  return (
    <div className="poll">
      <div className="poll-q"><BarChart3 size={15} /> {poll.question}</div>
      {poll.options.map((o, i) => {
        const n = Object.values(votes).filter((x) => x === i).length
        const pct = total ? Math.round((n / total) * 100) : 0
        return (
          <button key={i} type="button" className={`poll-opt ${mine === i ? 'on' : ''}`} onClick={() => vote(i)}>
            <i style={{ width: `${pct}%` }} />
            <span className="poll-t">{mine === i ? <PosterIcon name="check" size={14} style={{ marginRight: 5 }} /> : null}{o}</span>
            <span className="poll-n">{n}{total ? <em>{pct}%</em> : null}</span>
          </button>
        )
      })}
      <small className="poll-foot">{total} vote{total === 1 ? '' : 's'} · tap again to remove</small>
    </div>
  )
}

// ---------------------------------------------------------------- Group voice room
export function GroupVoiceRoom({ groupId, meUid, members, onClose }: {
  groupId: string; meUid: string; members: { uid: string; name: string; avatar: string }[]; onClose: () => void
}) {
  const v = useGroupVoice(groupId, meUid, true)
  const byId = useMemo(() => new Map(members.map((m) => [m.uid, m])), [members])
  const people = [meUid, ...Array.from(v.connected)].filter((x, i, a) => a.indexOf(x) === i)
  return (
    <Sheet title="Voice room" icon={<Users size={16} />} onClose={() => { v.leave(); onClose() }}>
      {!v.supported && <p className="hub-err">Voice is not available on this device.</p>}
      {v.error && <p className="hub-err">{v.error}</p>}
      <p className="hub-note">{v.joined ? 'You are live. Friends who tap "Join" in this group will connect with you.' : 'Talk live with the group. Everyone who joins is heard by everyone else. Up to 4 people work best.'}</p>
      {v.joined && (
        <div className="hub-voice">
          {people.map((uid) => {
            const m = byId.get(uid)
            return (
              <div key={uid} className={`hub-vp ${v.speaking.has(uid) ? 'talk' : ''}`}>
                <Av src={m?.avatar} name={m?.name || (uid === meUid ? 'You' : '?')} size={52} />
                <small>{uid === meUid ? 'You' : m?.name || 'Member'}</small>
              </div>
            )
          })}
        </div>
      )}
      <div className="hub-vbtns">
        {!v.joined
          ? <button type="button" className="hub-primary" disabled={!v.supported || v.joining} onClick={() => void v.join()}><Mic size={16} /> {v.joining ? 'Joining…' : 'Join voice room'}</button>
          : (
            <>
              <button type="button" className="hub-round" onClick={v.toggleMic} aria-label={v.micOn ? 'Mute' : 'Unmute'}>{v.micOn ? <Mic size={20} /> : <MicOff size={20} />}</button>
              <button type="button" className="hub-round end" onClick={() => { v.leave(); onClose() }} aria-label="Leave"><PhoneOff size={20} /></button>
            </>
          )}
      </div>
    </Sheet>
  )
}
